import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CitizenAccessDto, GrantCitizenAccessDto } from '@munifocus/contracts-gov';
import { api } from './api-client';
import { useMutationToast } from './mutation-toast';

export const citizenAccessKeys = {
  byFile: (fileId: string) => ['citizen-access', fileId] as const,
};

export async function listCitizenAccess(fileId: string): Promise<CitizenAccessDto[]> {
  const { data } = await api.get<CitizenAccessDto[]>(`/files/${fileId}/citizen-access`);
  return data;
}

export function useCitizenAccess(fileId: string) {
  return useQuery({
    queryKey: citizenAccessKeys.byFile(fileId),
    queryFn: () => listCitizenAccess(fileId),
    enabled: !!fileId,
  });
}

export function useGrantCitizenAccess(fileId: string) {
  const queryClient = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async (dto: GrantCitizenAccessDto) => {
      const { data } = await api.post<CitizenAccessDto[]>(`/files/${fileId}/citizen-access`, dto);
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(citizenAccessKeys.byFile(fileId), data);
      toast.success('gov.citizenAccess.grantedToast');
    },
    onError: (err) => toast.error(err, 'gov.citizenAccess.grantError'),
  });
}

export function useRevokeCitizenAccess(fileId: string) {
  const queryClient = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async (grantId: string) => {
      const { data } = await api.delete<CitizenAccessDto[]>(
        `/files/${fileId}/citizen-access/${grantId}`,
      );
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(citizenAccessKeys.byFile(fileId), data);
      toast.success('gov.citizenAccess.revokedToast');
    },
    onError: (err) => toast.error(err, 'gov.citizenAccess.revokeError'),
  });
}

/**
 * Set what a grant permits, without withdrawing the grant.
 *
 * Sends the full set the officer intends rather than a delta: switching
 * everything off is a decision they are entitled to make, and patch semantics
 * would make it indistinguishable from "no change".
 */
export function useSetCitizenCapabilities(fileId: string) {
  const queryClient = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async ({ grantId, capabilities }: { grantId: string; capabilities: string[] }) => {
      const { data } = await api.put<CitizenAccessDto[]>(
        `/files/${fileId}/citizen-access/${grantId}/capabilities`,
        { capabilities },
      );
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(citizenAccessKeys.byFile(fileId), data);
      // A toggle that changes what a member of the public can do should say it took.
      toast.success('gov.citizenAccess.capabilityToast');
    },
    onError: (err) => toast.error(err, 'gov.citizenAccess.capabilityError'),
  });
}
